import { getConditionDisplayName } from '../config/skinConditionDisplayNames';
import { normalizeConditionName } from './skinConditionNormalization';
import { CLASS_COLORS_MAP } from './skinResultsUtils';

const RADAR_SIZE = 380;
const RADAR_RADIUS = 108;

function buildAxisFromItems(items) {
  const cx = RADAR_SIZE / 2;
  const cy = RADAR_SIZE / 2;
  const radius = RADAR_RADIUS;
  const axis = items.map((item, idx) => {
    const angle = ((Math.PI * 2 * idx) / items.length) - Math.PI / 2;
    const dataRadius = (item.intensity / 100) * radius;
    const pointX = cx + Math.cos(angle) * dataRadius;
    const pointY = cy + Math.sin(angle) * dataRadius;
    const cos = Math.cos(angle);
    const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
    const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
    const labelX = cx + Math.cos(angle) * (radius + 14);
    const labelY = cy + Math.sin(angle) * (radius + 14);
    return {
      ...item,
      angle,
      pointX,
      pointY,
      labelX,
      labelY,
      labelAnchor,
      labelOffsetX,
    };
  });
  return {
    size: RADAR_SIZE,
    cx,
    cy,
    radius,
    axis,
    polygon: axis.map((a) => `${a.pointX},${a.pointY}`).join(' '),
    levels: [20, 40, 60, 80, 100],
  };
}

export function buildConcernRadarItems(concernsList = []) {
  return [...concernsList]
    .sort((a, b) => a.score - b.score)
    .map((item) => ({
      ...item,
      label: getConditionDisplayName(item.class_name),
      intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
    }));
}

export function buildRadarModel(concernRadarItems) {
  if (!concernRadarItems?.length) return null;
  return buildAxisFromItems(concernRadarItems);
}

export function buildPreviousConcernRadarItems(previousSkinData) {
  const prevScoreObj = previousSkinData?.skinScore ?? previousSkinData?.skin_score;
  const prevLabelScores = prevScoreObj?.skin_label_scores ?? previousSkinData?.skin_label_scores ?? null;
  if (!prevLabelScores || typeof prevLabelScores !== 'object') return [];
  return Object.entries(prevLabelScores)
    .map(([name, score]) => {
      let raw = score;
      if (raw && typeof raw === 'object') {
        raw = raw.score ?? raw.value ?? raw.percentage ?? raw.percent ?? 0;
      }
      const numeric = typeof raw === 'number' ? raw : parseFloat(raw) || 0;
      const className = normalizeConditionName(String(name || ''));
      return {
        class_name: className,
        score: Math.round(numeric),
        color: CLASS_COLORS_MAP[className] || 'rgb(15, 118, 110)',
      };
    })
    .filter((item) => item.class_name)
    .sort((a, b) => a.score - b.score)
    .map((item) => ({
      ...item,
      label: getConditionDisplayName(item.class_name),
      intensity: Math.max(5, Math.min(100, Number(item.score || 0))),
    }));
}

export function buildPreviousRadarModel(previousConcernRadarItems) {
  if (!previousConcernRadarItems?.length) return null;
  return buildAxisFromItems(previousConcernRadarItems);
}

export function buildRadarComparisonModel(concernRadarItems, previousConcernRadarItems) {
  if (!concernRadarItems?.length || !previousConcernRadarItems?.length) return null;

  const cx = RADAR_SIZE / 2;
  const cy = RADAR_SIZE / 2;
  const radius = RADAR_RADIUS;

  const afterMap = new Map(concernRadarItems.map((item) => [normalizeConditionName(item.class_name), item]));
  const beforeMap = new Map(previousConcernRadarItems.map((item) => [normalizeConditionName(item.class_name), item]));
  const orderedKeys = [
    ...Array.from(afterMap.keys()),
    ...Array.from(beforeMap.keys()).filter((key) => !afterMap.has(key)),
  ];

  const axis = orderedKeys.map((key, idx) => {
    const afterItem = afterMap.get(key) || null;
    const beforeItem = beforeMap.get(key) || null;
    const angle = ((Math.PI * 2 * idx) / orderedKeys.length) - Math.PI / 2;
    const afterIntensity = afterItem ? Math.max(5, Math.min(100, Number(afterItem.score || 0))) : 0;
    const beforeIntensity = beforeItem ? Math.max(5, Math.min(100, Number(beforeItem.score || 0))) : 0;
    const afterRadius = (afterIntensity / 100) * radius;
    const beforeRadius = (beforeIntensity / 100) * radius;
    const cos = Math.cos(angle);
    const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
    const labelOffsetX = cos > 0.35 ? 3 : cos < -0.35 ? -3 : 0;
    const beforeScore = beforeItem ? Math.round(Number(beforeItem.score || 0)) : null;
    const afterScore = afterItem ? Math.round(Number(afterItem.score || 0)) : null;
    const delta = Number.isFinite(beforeScore) && Number.isFinite(afterScore)
      ? Math.round((afterScore - beforeScore) * 10) / 10
      : null;
    const changeKind = !beforeItem && afterItem
      ? 'new'
      : beforeItem && !afterItem
        ? 'missing'
        : Number.isFinite(delta)
          ? delta >= 3
            ? 'improved'
            : delta <= -3
              ? 'declined'
              : 'stable'
          : 'stable';

    return {
      key,
      label: getConditionDisplayName(afterItem?.class_name || beforeItem?.class_name || key),
      angle,
      hasAfter: Boolean(afterItem),
      hasBefore: Boolean(beforeItem),
      afterScore,
      beforeScore,
      delta,
      changeKind,
      afterPointX: cx + Math.cos(angle) * afterRadius,
      afterPointY: cy + Math.sin(angle) * afterRadius,
      beforePointX: cx + Math.cos(angle) * beforeRadius,
      beforePointY: cy + Math.sin(angle) * beforeRadius,
      labelX: cx + Math.cos(angle) * (radius + 18),
      labelY: cy + Math.sin(angle) * (radius + 18),
      labelAnchor,
      labelOffsetX,
    };
  });

  return {
    size: RADAR_SIZE,
    cx,
    cy,
    radius,
    levels: [20, 40, 60, 80, 100],
    axis,
    afterPolygon: axis.map((a) => `${a.afterPointX},${a.afterPointY}`).join(' '),
    beforePolygon: axis.map((a) => `${a.beforePointX},${a.beforePointY}`).join(' '),
  };
}

export function getPreviousOverallSkinScore(previousSkinData) {
  if (!previousSkinData) return null;
  const prevRaw = previousSkinData?.skinScore?.final_skin_score
    ?? previousSkinData?.skinScore?.score
    ?? previousSkinData?.skin_score?.final_skin_score
    ?? previousSkinData?.skin_score?.score
    ?? null;
  if (prevRaw == null || Number.isNaN(Number(prevRaw))) return null;
  return Math.max(0, Math.min(100, Math.round(Number(prevRaw))));
}
